import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import ImageIcon from '@material-ui/icons/Image';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import DescriptionIcon from '@material-ui/icons/Description';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import config from '../../../Config';

/**
 * Direct file download helper that triggers a download immediately
 * without opening a new tab or redirecting the current page.
 */
export const downloadFileDirect = async (fileOrUrl, customFileName = '') => {
  if (!fileOrUrl) return;

  if (typeof window !== 'undefined' && fileOrUrl instanceof File) {
    const blobUrl = window.URL.createObjectURL(fileOrUrl);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = blobUrl;
    a.download = fileOrUrl.name || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
    return;
  }

  let fileUrl = '';
  let fileName = customFileName;

  if (typeof fileOrUrl === 'string') {
    fileUrl = fileOrUrl;
    if (!fileName) {
      fileName = fileUrl.split('/').pop().split('?')[0] || 'download';
    }
  } else if (typeof fileOrUrl === 'object') {
    fileUrl = fileOrUrl.url || '';
    fileName = customFileName || fileOrUrl.name || fileOrUrl.original_name || (fileUrl ? fileUrl.split('/').pop().split('?')[0] : 'download');
  }

  if (!fileUrl) return;

  // Build the backend attachment download endpoint URL if it's a storage file
  const backendBase = config.apiurl ? config.apiurl.replace(/\/+$/, '') : '';
  const downloadEndpoint = `${backendBase}/api/tasks/download-file?file=${encodeURIComponent(fileUrl)}&name=${encodeURIComponent(fileName)}`;

  try {
    // 1. Attempt to fetch as blob with CORS
    const response = await fetch(fileUrl, { mode: 'cors' }).catch(() => null);
    if (response && response.ok) {
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
      return;
    }

    // 2. If direct fetch fails, fetch through backend download endpoint
    const apiRes = await fetch(downloadEndpoint, { mode: 'cors' }).catch(() => null);
    if (apiRes && apiRes.ok) {
      const blob = await apiRes.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
      return;
    }
  } catch (err) {
    console.warn('Direct blob download error, falling back to hidden frame:', err);
  }

  // 3. Fallback: Hidden iframe navigation (triggers download via Content-Disposition without page redirect)
  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = downloadEndpoint;
    document.body.appendChild(iframe);
    setTimeout(() => {
      try {
        document.body.removeChild(iframe);
      } catch (_) {}
    }, 30000);
  } catch (_) {
    // 4. Last resort
    const a = document.createElement('a');
    a.href = downloadEndpoint;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
};

/**
 * Reusable File Preview Dialog for in-page viewing (Lightbox modal)
 */
const FilePreviewDialog = ({ open, file, onClose, onDownload, zIndex = 3200 }) => {
  if (!file) return null;

  const fileUrl = typeof file === 'string' ? file : file.url;
  const fileName = (typeof file === 'object' && (file.name || file.original_name)) ? (file.name || file.original_name) : (fileUrl ? fileUrl.split('/').pop().split('?')[0] : 'File Preview');

  const ext = (fileName.split('.').pop() || '').toLowerCase();
  const isImage = (typeof file === 'object' && ((file.type && file.type.startsWith('image/')) || (file.mime_type && file.mime_type.startsWith('image/')))) ||
    ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg', 'jfif', 'ico', 'avif', 'tiff', 'tif'].includes(ext);
  const isPdf = (typeof file === 'object' && ((file.type === 'application/pdf') || (file.mime_type === 'application/pdf'))) || ext === 'pdf';
  const isWord = ['doc', 'docx'].includes(ext);
  const isExcel = ['xls', 'xlsx', 'csv'].includes(ext);

  const handleDownload = () => {
    if (onDownload) {
      onDownload(file);
    } else {
      downloadFileDirect(file);
    }
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      style={{ zIndex }}
      PaperProps={{
        style: {
          borderRadius: 12,
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <DialogTitle
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 18px',
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
        }}
        disableTypography
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1, paddingRight: 10 }}>
          {isImage ? (
            <ImageIcon style={{ color: '#0284c7', fontSize: 24, flexShrink: 0 }} />
          ) : isPdf ? (
            <PictureAsPdfIcon style={{ color: '#ef4444', fontSize: 24, flexShrink: 0 }} />
          ) : isWord || isExcel ? (
            <DescriptionIcon style={{ color: '#2563eb', fontSize: 24, flexShrink: 0 }} />
          ) : (
            <InsertDriveFileIcon style={{ color: '#64748b', fontSize: 24, flexShrink: 0 }} />
          )}
          <Typography
            variant="h6"
            style={{
              fontSize: '15px',
              fontWeight: 600,
              color: '#1e293b',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={fileName}
          >
            {fileName}
          </Typography>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <Tooltip title="Download file" arrow>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDownload}
              startIcon={<GetAppIcon style={{ fontSize: 18 }} />}
              style={{
                textTransform: 'none',
                borderColor: '#cbd5e1',
                color: '#036dda',
                fontWeight: 600,
                fontSize: 12,
                borderRadius: 6,
                padding: '4px 10px',
              }}
            >
              Download
            </Button>
          </Tooltip>
          <Tooltip title="Close (Esc)" arrow>
            <IconButton
              size="small"
              onClick={onClose}
              style={{ color: '#64748b', padding: 5 }}
            >
              <CloseIcon style={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </div>
      </DialogTitle>

      <DialogContent
        style={{
          padding: 16,
          backgroundColor: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: isImage ? 340 : 480,
          maxHeight: '82vh',
          overflow: 'auto',
        }}
      >
        {isImage ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              backgroundColor: '#0f172a',
              borderRadius: 8,
              padding: 12,
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <img
              src={fileUrl}
              alt={fileName}
              style={{
                maxWidth: '100%',
                maxHeight: '74vh',
                objectFit: 'contain',
                borderRadius: 6,
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
              }}
            />
          </div>
        ) : isPdf ? (
          <iframe
            src={fileUrl}
            title={fileName}
            style={{
              width: '100%',
              height: '75vh',
              border: 'none',
              borderRadius: 8,
              backgroundColor: '#ffffff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          />
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: 40,
              backgroundColor: '#ffffff',
              borderRadius: 12,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              maxWidth: 400,
              width: '100%',
            }}
          >
            <div style={{ marginBottom: 16 }}>
              {isWord ? (
                <DescriptionIcon style={{ fontSize: 64, color: '#2563eb' }} />
              ) : isExcel ? (
                <DescriptionIcon style={{ fontSize: 64, color: '#16a34a' }} />
              ) : (
                <InsertDriveFileIcon style={{ fontSize: 64, color: '#64748b' }} />
              )}
            </div>
            <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#1e293b', marginBottom: 6, wordBreak: 'break-word' }}>
              {fileName}
            </Typography>
            <Typography variant="body2" style={{ color: '#64748b', marginBottom: 20 }}>
              {ext.toUpperCase()} document cannot be previewed directly in browser.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownload}
              startIcon={<GetAppIcon />}
              style={{
                backgroundColor: '#036dda',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 8,
                padding: '8px 20px',
              }}
            >
              Download File
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FilePreviewDialog;
